import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "components/Layout";
import Calendar, { toISODate } from "components/Calendar";
import api from "utils/api";
import { DR_HOFFMANN_SERVICES, formatPrice } from "constants/massages";
import "styles/booking.css";

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
const formatLong = (date) =>
  date.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

const OPTIONS = [
  {
    id: "consultation",
    serviceKey: "dr-hoffmann-consultation",
    tag: "Option 1",
    title: "Lizenzpartner werden",
    description:
      "Kostenloser Erstbesuch inkl. bis zu 6 Test-Massagen und Location-Check. Kein Login nötig.",
    price: "ab 900 €",
    priceNote: "(Starterpaket nach Beratung)",
    requiresAuth: false,
    requiresAddress: false,
    bookingMode: "consultation",
    durationMinutes: 30,
    closedWeekday: null,
  },
  {
    id: "studio",
    serviceKey: "dr-hoffmann-studio",
    tag: "Option 2",
    title: "Massage im Studio buchen",
    description:
      "10-Minuten Perkussiv-Massage im Studio (aktuell Tavon Spa, Hopfenmarkt 31). Bezahlung vor Ort.",
    price: "29 €",
    priceNote: "/ 10 Min.",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "studio",
    durationMinutes: 10,
    closedWeekday: 2,
  },
  {
    id: "mobile",
    serviceKey: "dr-hoffmann-mobile",
    tag: "Option 3",
    title: "Massage Vor Ort buchen",
    description:
      "Unser Team kommt zu Ihnen — Büro, Event, Zuhause. Nur im Hamburger Stadtgebiet (PLZ-Check).",
    price: "39 €",
    priceNote: "/ 10 Min.",
    requiresAuth: true,
    requiresAddress: true,
    bookingMode: "mobile",
    durationMinutes: 10,
    closedWeekday: null,
  },
];

const phoneRegex = /^[+\d][\d\s\-()/]{5,24}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BookingPage = () => {
  const user = useSelector((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const initialOptionId = params.get("option") || null;
  const initialOption = OPTIONS.find((o) => o.id === initialOptionId) || null;

  const [selectedOption, setSelectedOption] = useState(initialOption);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Consultation-form fields (used only when option = consultation).
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    notes: "",
  });
  // Address fields (mobile option).
  const [address, setAddress] = useState({
    companyName: "",
    street: "",
    postalCode: "",
    city: "",
    notes: "",
  });
  // Allowed PLZ prefixes (loaded on first mobile-option open).
  const [postalRules, setPostalRules] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const allowedPrefixes = useMemo(
    () => postalRules?.allowedPrefixes || ["20", "21", "22"],
    [postalRules]
  );

  const plzIsValid =
    /^\d{5}$/.test(address.postalCode) &&
    allowedPrefixes.some((p) => address.postalCode.startsWith(p));

  // Lazy-load postal rules whenever the user opens the mobile flow.
  useEffect(() => {
    if (selectedOption?.bookingMode !== "mobile") return undefined;
    if (postalRules) return undefined;
    let cancelled = false;
    api
      .get("/bookings/postal-rules")
      .then(({ data }) => {
        if (!cancelled && data?.allowedPrefixes) setPostalRules(data);
      })
      .catch(() => {
        if (!cancelled) setPostalRules({ allowedPrefixes: ["20", "21", "22"] });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOption, postalRules]);

  // Reload slots when the date or option changes.
  useEffect(() => {
    if (!selectedOption || !selectedDate) {
      setSlots([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingSlots(true);
    setSelectedSlot(null);
    api
      .get("/bookings/availability", {
        params: {
          date: toISODate(selectedDate),
          durationMinutes: selectedOption.durationMinutes,
        },
      })
      .then(({ data }) => {
        if (!cancelled) setSlots(data?.slots || []);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDate, selectedOption]);

  const handleSelectOption = (option) => {
    if (option.requiresAuth && !user) return;
    setSelectedOption(option);
    setSelectedDate(null);
    setSelectedSlot(null);
    setError(null);
    navigate(`/buchen?option=${option.id}`, { replace: true });
  };

  const updateForm = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const updateAddress = (key) => (e) =>
    setAddress((prev) => ({ ...prev, [key]: e.target.value }));

  // ─── Submit handlers ──────────────────────────────────────────────
  const submitConsultation = async () => {
    setError(null);
    if (!form.firstName || !form.lastName) {
      return setError("Bitte gib Vor- und Nachname an.");
    }
    if (!emailRegex.test(form.email)) {
      return setError("Bitte gib eine gültige E-Mail-Adresse an.");
    }
    if (!phoneRegex.test(form.phone)) {
      return setError("Bitte gib eine gültige Telefonnummer an.");
    }
    if (!selectedSlot) {
      return setError("Bitte wähle einen Termin.");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/consultation", {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        companyName: form.companyName,
        notes: form.notes,
        startISO: selectedSlot,
      });
      navigate(`/buchen/erfolg?bookingId=${data.booking._id}`, {
        state: { booking: data.booking },
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Die Buchung konnte nicht angelegt werden.");
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  const submitAuthedBooking = async () => {
    setError(null);
    if (!user) {
      const params = new URLSearchParams({ next: `${location.pathname}${location.search}` });
      navigate(`/einloggen?${params.toString()}`);
      return;
    }
    if (!selectedSlot) {
      return setError("Bitte wähle einen Termin.");
    }
    if (selectedOption.bookingMode === "mobile") {
      if (!address.street || !address.postalCode || !address.city) {
        return setError("Bitte gib die vollständige Adresse an.");
      }
      if (!plzIsValid) {
        return setError(
          "Diese Postleitzahl liegt aktuell nicht im Liefergebiet. Wir starten in Hamburg und erweitern PLZ-basiert."
        );
      }
    }
    setSubmitting(true);
    try {
      const paymentMethod = selectedOption.bookingMode === "mobile" ? "stripe" : "on-site";
      const body = {
        serviceKey: selectedOption.serviceKey,
        durationMinutes: selectedOption.durationMinutes,
        startISO: selectedSlot,
        paymentMethod,
      };
      if (selectedOption.bookingMode === "mobile") {
        body.serviceAddress = address;
      }
      const { data } = await api.post("/bookings", body);
      const booking = data.booking;
      if (
        booking?.paymentMethod === "stripe" &&
        booking?.paymentStatus === "pending" &&
        booking?.priceCents > 0
      ) {
        try {
          const { data: checkout } = await api.post(
            `/payment/checkout/${booking._id}`
          );
          if (checkout?.url) {
            window.location.href = checkout.url;
            return;
          }
        } catch {
          // fall through to success page
        }
      }
      navigate(`/buchen/erfolg?bookingId=${booking._id}`, {
        state: { booking },
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Die Buchung konnte nicht angelegt werden."
      );
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  return (
    <Layout>
      <section className="booking-page">
        <h1 className="booking-title">
          Jetzt <em>Termin</em> buchen
        </h1>
        <p className="booking-intro">
          Wählen Sie die passende Option. Für den kostenlosen Lizenzpartner-Erstbesuch
          ist kein Konto nötig — für Studio- und Vor-Ort-Buchungen ist eine
          Anmeldung erforderlich.
        </p>

        <div className="option-grid">
          {OPTIONS.map((option) => {
            const locked = option.requiresAuth && !user;
            const isSelected = selectedOption?.id === option.id;
            return (
              <div
                key={option.id}
                className={`option-card ${isSelected ? "is-selected" : ""} ${locked ? "is-locked" : ""}`}
              >
                <span className="option-tag">{option.tag}</span>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
                <div className="option-price">
                  {option.price} <small>{option.priceNote}</small>
                </div>
                <div className="option-actions">
                  {locked ? (
                    <>
                      <button type="button" className="btn-primary" disabled>
                        Anmeldung erforderlich
                      </button>
                      <div className="lock-note">
                        <Link to={`/einloggen?next=${encodeURIComponent("/buchen?option=" + option.id)}`}>
                          Anmelden
                        </Link>{" "}
                        oder{" "}
                        <Link to={`/registrieren?next=${encodeURIComponent("/buchen?option=" + option.id)}`}>
                          kostenlos registrieren
                        </Link>
                      </div>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={isSelected ? "btn-outline" : "btn-primary"}
                      onClick={() => handleSelectOption(option)}
                    >
                      {isSelected ? "Auswahl aktiv" : "Auswählen"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {selectedOption && (
          <div className="option-detail">
            <div className="booking-step">
              <div className="step-head">
                <h2>
                  {selectedOption.title} — <em>Datum &amp; Uhrzeit</em>
                </h2>
                <span className="booking-step-number">01</span>
              </div>
              <p className="step-summary">
                {selectedOption.bookingMode === "consultation"
                  ? "Wir kommen zu Ihnen vor Ort. Wählen Sie einen Wunschtermin — wir bestätigen kurzfristig."
                  : "Termine sind frühestens 1 Stunde im Voraus und bis zu 2 Monate im Voraus buchbar."}
              </p>
              <Calendar
                selectedDate={selectedDate}
                onSelect={setSelectedDate}
                minDate={new Date()}
                maxDate={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)}
                closedWeekday={selectedOption.closedWeekday ?? -1}
              />
              {selectedDate && (
                <>
                  {loadingSlots ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      Verfügbare Zeiten werden geladen…
                    </p>
                  ) : slots.length === 0 ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      Für den gewählten Tag sind aktuell keine Slots mehr frei. Bitte einen anderen Tag wählen.
                    </p>
                  ) : (
                    <div className="slot-grid">
                      {slots.map((iso) => (
                        <button
                          type="button"
                          key={iso}
                          onClick={() => setSelectedSlot(iso)}
                          className={`slot-button ${selectedSlot === iso ? "is-selected" : ""}`}
                        >
                          {formatTime(iso)}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {selectedOption.bookingMode === "consultation" && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    Ihre <em>Kontaktdaten</em>
                  </h2>
                  <span className="booking-step-number">02</span>
                </div>
                <p className="step-summary">
                  Wir senden Ihnen eine Bestätigung per E-Mail. Pflichtfelder sind markiert.
                </p>
                <div className="address-grid">
                  <div>
                    <label htmlFor="cf-first">Vorname *</label>
                    <input id="cf-first" value={form.firstName} onChange={updateForm("firstName")} />
                  </div>
                  <div>
                    <label htmlFor="cf-last">Nachname *</label>
                    <input id="cf-last" value={form.lastName} onChange={updateForm("lastName")} />
                  </div>
                  <div>
                    <label htmlFor="cf-email">E-Mail *</label>
                    <input id="cf-email" type="email" value={form.email} onChange={updateForm("email")} />
                  </div>
                  <div>
                    <label htmlFor="cf-phone">Telefon *</label>
                    <input id="cf-phone" type="tel" value={form.phone} onChange={updateForm("phone")} />
                  </div>
                  <div className="full">
                    <label htmlFor="cf-company">Studio / Firma (optional)</label>
                    <input id="cf-company" value={form.companyName} onChange={updateForm("companyName")} />
                  </div>
                  <div className="full">
                    <label htmlFor="cf-notes">Hinweise (optional)</label>
                    <textarea
                      id="cf-notes"
                      rows={3}
                      maxLength={500}
                      value={form.notes}
                      onChange={updateForm("notes")}
                      placeholder="Standort, Wünsche, beste Erreichbarkeit…"
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedOption.bookingMode === "mobile" && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    Ihre <em>Adresse</em>
                  </h2>
                  <span className="booking-step-number">02</span>
                </div>
                <p className="step-summary">
                  Wir kommen zu Ihnen. Pflichtfelder: Straße, PLZ, Ort. PLZ muss
                  im Hamburger Stadtgebiet liegen (
                  {allowedPrefixes.map((p) => `${p}xxx`).join(", ")}).
                </p>
                <div className="address-grid">
                  <div className="full">
                    <label htmlFor="ad-company">Name / Firma *</label>
                    <input id="ad-company" value={address.companyName} onChange={updateAddress("companyName")} />
                  </div>
                  <div className="full">
                    <label htmlFor="ad-street">Straße &amp; Hausnummer *</label>
                    <input id="ad-street" value={address.street} onChange={updateAddress("street")} />
                  </div>
                  <div>
                    <label htmlFor="ad-plz">PLZ *</label>
                    <input
                      id="ad-plz"
                      inputMode="numeric"
                      maxLength={5}
                      value={address.postalCode}
                      onChange={updateAddress("postalCode")}
                    />
                    {address.postalCode.length === 5 && (
                      <div className={`plz-status ${plzIsValid ? "is-ok" : "is-bad"}`}>
                        {plzIsValid
                          ? "PLZ liegt im Liefergebiet."
                          : "PLZ aktuell nicht im Liefergebiet."}
                      </div>
                    )}
                  </div>
                  <div>
                    <label htmlFor="ad-city">Ort *</label>
                    <input id="ad-city" value={address.city} onChange={updateAddress("city")} />
                  </div>
                  <div className="full">
                    <label htmlFor="ad-notes">Hinweise (optional)</label>
                    <textarea
                      id="ad-notes"
                      rows={3}
                      maxLength={300}
                      value={address.notes}
                      onChange={updateAddress("notes")}
                      placeholder="Stockwerk, Klingelschild, Hinweise zum Ablauf…"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="booking-step">
              <div className="step-head">
                <h2>
                  Ihre <em>Buchung</em>
                </h2>
                <span className="booking-step-number">
                  {selectedOption.bookingMode === "consultation" ? "03" : selectedOption.bookingMode === "mobile" ? "03" : "02"}
                </span>
              </div>
              <div className="booking-summary-block" style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>
                <div className="summary-row">
                  <span>Leistung</span>
                  <strong>{selectedOption.title}</strong>
                </div>
                <div className="summary-row">
                  <span>Dauer</span>
                  <strong>{selectedOption.durationMinutes} Min.</strong>
                </div>
                <div className="summary-row">
                  <span>Datum</span>
                  <strong>{selectedDate ? formatLong(selectedDate) : "—"}</strong>
                </div>
                <div className="summary-row">
                  <span>Uhrzeit</span>
                  <strong>{selectedSlot ? formatTime(selectedSlot) : "—"}</strong>
                </div>
                {selectedOption.bookingMode !== "mobile" && (
                  <div className="summary-row">
                    <span>Ort</span>
                    <strong>
                      {selectedOption.bookingMode === "consultation"
                        ? "Vor Ort beim Lizenzpartner"
                        : "Tavon Spa · Hopfenmarkt 31, Hamburg"}
                    </strong>
                  </div>
                )}
                <div className="summary-row">
                  <span>Zahlung</span>
                  <strong>
                    {selectedOption.bookingMode === "consultation"
                      ? "Kostenfrei"
                      : selectedOption.bookingMode === "mobile"
                        ? "Online (Stripe)"
                        : "Vor Ort"}
                  </strong>
                </div>
                <div className="total">
                  <span>Gesamt</span>
                  <strong>
                    {DR_HOFFMANN_SERVICES.find((s) => s.key === selectedOption.serviceKey)?.durations[0]?.priceCents === 0
                      ? "0 €"
                      : formatPrice(
                          DR_HOFFMANN_SERVICES.find((s) => s.key === selectedOption.serviceKey)?.durations[0]?.priceCents || 0
                        )}
                  </strong>
                </div>
              </div>

              {error && (
                <div className="auth-alert is-error" style={{ marginTop: "1rem" }}>
                  {error}
                </div>
              )}

              <div className="booking-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setSelectedOption(null);
                    navigate("/buchen", { replace: true });
                  }}
                >
                  Andere Option
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={submitting || !selectedSlot}
                  onClick={
                    selectedOption.bookingMode === "consultation"
                      ? submitConsultation
                      : submitAuthedBooking
                  }
                >
                  {submitting
                    ? "Buchung läuft…"
                    : selectedOption.bookingMode === "consultation"
                      ? "Kostenfreien Termin anfragen"
                      : selectedOption.bookingMode === "mobile"
                        ? "Zur Zahlung (Stripe)"
                        : "Jetzt verbindlich buchen"}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default BookingPage;
